// Sprint F · Feature 1 — Timeline view (work-stream matrix).
//
// Rows = projects, columns = the 9 hardcoded DC-development work-streams.
// Each cell shows a status icon + owner initials; click opens a detail
// panel where status/owner/target date/notes can be edited. Updates are
// PATCHed to /api/v1/projects/{id} via the existing updateProject
// mutation (workstreams live as a JSONB column on site_projects).
import React, { useState, useRef, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';
import { useProjects } from '../contexts/ProjectsContext';
import { useTeam, getTeamUser } from '../contexts/TeamContext';
import {
  WORKSTREAMS, STATUS_DEFS, statusDef,
  normalizeWorkstreams,
} from '../utils/workstreams';
import '../styles/Timeline.css';

export function TimelineView() {
  const { projects, updateProject } = useProjects();
  const [openCell, setOpenCell] = useState(null); // { project, workstream, anchor }

  const liveProjects = projects.filter(p => p.stage !== 'dropped');

  if (liveProjects.length === 0) {
    return (
      <div className="tl-empty">
        <div className="tl-empty-title">No projects yet</div>
        <div className="tl-empty-text">
          Save a site from the map to see it appear in the work-stream matrix.
        </div>
      </div>
    );
  }

  return (
    <div className="tl-wrap">
      <div className="tl-legend">
        {STATUS_DEFS.filter(s => s.key !== 'na').map(s => (
          <span key={s.key} className="tl-legend-item">
            <span className="tl-legend-dot" style={{ background: s.color }} />
            {s.label}
          </span>
        ))}
      </div>

      <div className="tl-table-wrap">
        <table className="tl-table">
          <thead>
            <tr>
              <th className="tl-corner">Project</th>
              {WORKSTREAMS.map(ws => (
                <th key={ws.key} className="tl-ws-head" title={ws.label}>
                  {ws.short}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {liveProjects.map(p => {
              const wss = normalizeWorkstreams(p);
              return (
                <tr key={p.id}>
                  <td className="tl-project-cell">
                    <a href={`#/projects/${p.id}`} className="tl-project-name">
                      {p.site_label}
                    </a>
                    <div className="tl-project-meta">
                      {(p.enrichment?.total_area_ha || p.enrichment?.areal_ha) ?
                        `${Math.round(p.enrichment.total_area_ha || p.enrichment.areal_ha)} ha · ` : ''}
                      {p.enrichment?.nearest_grid_voltage_kv ?
                        `${p.enrichment.nearest_grid_voltage_kv} kV` : ''}
                    </div>
                  </td>
                  {wss.map(ws => (
                    <StatusCell
                      key={ws.key}
                      project={p}
                      workstream={ws}
                      onClick={(rect) => setOpenCell({ project: p, workstream: ws, anchor: rect })}
                    />
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {openCell && (
        <WorkstreamDetailPopover
          project={openCell.project}
          workstream={openCell.workstream}
          anchor={openCell.anchor}
          onClose={() => setOpenCell(null)}
          onSave={async (patch) => {
            const target = openCell.project;
            const others = (target.workstreams || []).filter(w => w.key !== openCell.workstream.key);
            const updated = {
              ...openCell.workstream,
              ...patch,
              key: openCell.workstream.key,
              last_updated: new Date().toISOString(),
            };
            await updateProject(target.id, { workstreams: [...others, updated] });
            setOpenCell(null);
          }}
        />
      )}
    </div>
  );
}

function StatusCell({ workstream, onClick }) {
  const def = statusDef(workstream.status);
  const owner = workstream.owner_user_id ? getTeamUser(workstream.owner_user_id) : null;
  const overdue = workstream.target_completion &&
    workstream.status !== 'complete' &&
    new Date(workstream.target_completion) < new Date();

  return (
    <td
      className={`tl-cell tl-cell-${workstream.status}`}
      onClick={(e) => onClick(e.currentTarget.getBoundingClientRect())}
    >
      <div className="tl-cell-icon">{def.icon}</div>
      {owner && (
        <div className="tl-cell-owner" style={{ background: owner.color }}
             title={owner.name}>
          {owner.initials}
        </div>
      )}
      {overdue && <div className="tl-cell-flag">⚠ overdue</div>}
      {workstream.status === 'blocked' && workstream.notes && (
        <div className="tl-cell-flag">⚠ {workstream.notes.slice(0, 24)}</div>
      )}
    </td>
  );
}

function WorkstreamDetailPopover({ project, workstream, anchor, onClose, onSave }) {
  const { team } = useTeam();
  const [draft, setDraft] = useState({
    status: workstream.status,
    owner_user_id: workstream.owner_user_id || '',
    target_completion: workstream.target_completion || '',
    notes: workstream.notes || '',
  });

  // Click-outside / Escape to close (without saving).
  React.useEffect(() => {
    const onClick = (e) => {
      if (e.target.closest && e.target.closest('.tl-pop')) return;
      onClose();
    };
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [onClose]);

  const POP_W = 360;
  const MARGIN = 8;

  // First render places the popover BELOW the cell (best-guess); a
  // layout effect below measures actual height and repositions if it
  // would spill off the bottom or top edge. That two-pass approach
  // means the pop never flashes off-screen — we set visibility:hidden
  // for the first paint, measure, then reveal at the corrected spot.
  const popRef = useRef(null);
  const [pos, setPos] = useState(() => {
    const left = Math.min(
      Math.max(MARGIN, anchor.left + anchor.width / 2 - POP_W / 2),
      window.innerWidth - POP_W - MARGIN,
    );
    return { top: anchor.bottom + 6, left, ready: false };
  });

  useLayoutEffect(() => {
    const el = popRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const vh = window.innerHeight;
    const spaceBelow = vh - anchor.bottom - MARGIN;
    const spaceAbove = anchor.top - MARGIN;
    let top;
    if (rect.height + 6 <= spaceBelow) {
      top = anchor.bottom + 6;                              // fits below (preferred)
    } else if (rect.height + 6 <= spaceAbove) {
      top = anchor.top - rect.height - 6;                   // fits above → flip
    } else {
      // Doesn't fit either way (tall popover / short viewport). Anchor
      // to the taller side and clamp; the popover's own scroll-y takes
      // over from there so the user can still see all rows.
      top = spaceBelow >= spaceAbove
        ? Math.max(MARGIN, anchor.bottom + 6)
        : Math.max(MARGIN, vh - rect.height - MARGIN);
    }
    // Final clamp so top-edge is never negative and bottom-edge never
    // exceeds vh (belt + braces for future content changes).
    top = Math.max(MARGIN, Math.min(top, vh - rect.height - MARGIN));

    const left = Math.min(
      Math.max(MARGIN, anchor.left + anchor.width / 2 - POP_W / 2),
      window.innerWidth - POP_W - MARGIN,
    );
    setPos({ top, left, ready: true });
  }, [anchor.top, anchor.bottom, anchor.left, anchor.width]);

  const wsLabel = WORKSTREAMS.find(w => w.key === workstream.key)?.label || workstream.key;

  return createPortal(
    <div
      ref={popRef}
      className="tl-pop"
      style={{
        top: pos.top,
        left: pos.left,
        width: POP_W,
        visibility: pos.ready ? 'visible' : 'hidden',
        maxHeight: `calc(100vh - ${MARGIN * 2}px)`,
        overflowY: 'auto',
      }}
    >
      <div className="tl-pop-header">
        <div>
          <div className="tl-pop-title">{wsLabel}</div>
          <div className="tl-pop-sub">{project.site_label}</div>
        </div>
        <button className="tl-pop-close" onClick={onClose}>×</button>
      </div>

      <div className="tl-pop-row">
        <label>Status</label>
        <select value={draft.status} onChange={e => setDraft({ ...draft, status: e.target.value })}>
          {STATUS_DEFS.map(s => (
            <option key={s.key} value={s.key}>{s.icon} {s.label}</option>
          ))}
        </select>
      </div>

      <div className="tl-pop-row">
        <label>Owner</label>
        <select value={draft.owner_user_id} onChange={e => setDraft({ ...draft, owner_user_id: e.target.value })}>
          <option value="">— unassigned —</option>
          {team.map(u => (
            <option key={u.id} value={u.id}>{u.name}</option>
          ))}
        </select>
      </div>

      <div className="tl-pop-row">
        <label>Target completion</label>
        <input type="date" value={draft.target_completion}
               onChange={e => setDraft({ ...draft, target_completion: e.target.value })} />
      </div>

      <div className="tl-pop-row tl-pop-row-stack">
        <label>Notes</label>
        <textarea rows={3} value={draft.notes}
                  onChange={e => setDraft({ ...draft, notes: e.target.value })}
                  placeholder="e.g. Fingrid liittymistarjous expected by 2026-05-15" />
      </div>

      <div className="tl-pop-footer">
        <button className="tl-pop-cancel" onClick={onClose}>Cancel</button>
        <button className="tl-pop-save" onClick={() => onSave(draft)}>Save</button>
      </div>
    </div>,
    document.body,
  );
}
